import React, { ReactNode, useMemo } from 'react'
import {
  DimensionValue,
  Dimensions,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native'
import { Text, useTheme } from 'react-native-paper'

/**
 * MelddModal - A GraphQL-safe modal component
 *
 * IMPORTANT: Use this modal instead of react-native-paper's Portal/Dialog when:
 * - You need to use GraphQL hooks (useQuery, useMutation) inside the modal
 * - You need to use React hooks that depend on component context
 *
 * Why?
 * react-native-paper's Portal/Dialog renders components outside the React tree,
 * which breaks React hooks and GraphQL hooks that rely on component context.
 * This modal uses React Native's Modal component directly, keeping everything
 * in the same React tree, allowing hooks to work correctly.
 *
 * @example
 * ```tsx
 * const [showModal, setShowModal] = useState(false)
 * const [createOrder] = useCreateOrderMutation() // ✅ Works!
 *
 * return (
 *   <MelddModal
 *     visible={showModal}
 *     onDismiss={() => setShowModal(false)}
 *     title="Create Order"
 *     body={<OrderForm />}
 *     actions={
 *       <Button onPress={handleSubmit}>Submit</Button>
 *     }
 *   />
 * )
 * ```
 */
export type MelddModalProps = {
  /**
   * Whether the modal is visible
   */
  visible: boolean
  /**
   * Callback when modal should be dismissed (e.g., backdrop press, back button)
   */
  onDismiss: () => void
  /**
   * Modal title text
   */
  title?: string
  /**
   * Modal body content (can include GraphQL hooks)
   */
  body: ReactNode
  /**
   * Action buttons or footer content
   */
  actions?: ReactNode
  /**
   * Custom width (defaults to responsive: 400px on web, 80% on mobile)
   */
  width?: number | string
  /**
   * Custom max width (defaults to '90%')
   */
  maxWidth?: number | string
  /**
   * Custom max height (defaults to '90%')
   */
  maxHeight?: number | string
  /**
   * When true, body is wrapped in a ScrollView and modal height is constrained
   * so content scrolls on small screens (e.g. maxHeight = screen height).
   */
  scrollable?: boolean
  /**
   * Merged into ScrollView contentContainerStyle when scrollable is true (e.g. flexGrow + justifyContent for vertical centering).
   */
  scrollContentContainerStyle?: StyleProp<ViewStyle>
  /**
   * Whether to show backdrop overlay (defaults to true)
   */
  showBackdrop?: boolean
  /**
   * Custom backdrop color (defaults to 'rgba(0, 0, 0, 0.5)')
   */
  backdropColor?: string
}

export const MelddModal = ({
  visible,
  onDismiss,
  title,
  body,
  actions,
  width,
  maxWidth = '90%',
  maxHeight = '90%',
  scrollable = false,
  scrollContentContainerStyle,
  showBackdrop = true,
  backdropColor = 'rgba(0, 0, 0, 0.5)',
}: MelddModalProps) => {
  const theme = useTheme()

  const styles = useMemo(
    () =>
      StyleSheet.create({
        overlay: {
          flex: 1,
          backgroundColor: showBackdrop ? backdropColor : 'transparent',
          justifyContent: 'center',
          alignItems: 'center',
        },
        modalContainer: {
          backgroundColor: theme.colors.surface,
          borderRadius: theme.roundness * 2,
          width: (width ||
            (Platform.OS === 'web' ? 400 : '80%')) as unknown as DimensionValue,
          maxWidth: maxWidth as unknown as DimensionValue,
          maxHeight: maxHeight as unknown as DimensionValue,
          ...(scrollable && {
            height: (typeof maxHeight === 'number'
              ? maxHeight
              : Dimensions.get('window').height *
                0.9) as unknown as DimensionValue,
            flexDirection: 'column',
          }),
          padding: 24,
          elevation: 8,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.25,
          shadowRadius: 8,
        },
        title: {
          fontSize: 20,
          fontWeight: 'bold',
          marginBottom: 16,
          color: theme.colors.onSurface,
        },
        content: {
          width: '100%',
        },
        contentScroll: {
          flex: 1,
          minHeight: 0,
        },
        actions: {
          flexDirection: 'row',
          justifyContent: 'flex-end',
          marginTop: 16,
          gap: 8,
        },
      }),
    [theme, width, maxWidth, maxHeight, scrollable, showBackdrop, backdropColor]
  )

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onDismiss}
    >
      <Pressable style={styles.overlay} onPress={onDismiss}>
        <Pressable
          style={styles.modalContainer}
          onPress={(e) => e.stopPropagation()}
        >
          {title && <Text style={styles.title}>{title}</Text>}
          {scrollable ? (
            <View style={styles.contentScroll}>
              <ScrollView
                style={styles.contentScroll}
                contentContainerStyle={[
                  styles.content,
                  scrollContentContainerStyle,
                ]}
                showsVerticalScrollIndicator={true}
                keyboardShouldPersistTaps="handled"
              >
                {body}
              </ScrollView>
            </View>
          ) : (
            <View style={styles.content}>{body}</View>
          )}
          {actions && <View style={styles.actions}>{actions}</View>}
        </Pressable>
      </Pressable>
    </Modal>
  )
}
